import { useMemo, useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatMoney } from '@/lib/money';
import { PAYSLIPS_BASE } from '../api';
import { CabinetEmptyState, CabinetListFooter, CabinetListToolbar } from '../components/listChrome';
import { createCabinetViewStore, viewModes } from '../store/cabinetView';
import { useCabinetPayslips } from '../queries';
import type { CabinetPayslip } from '../types';

const usePayslipUi = createCabinetViewStore('cabinet.payslips:ui', 'table');
const MODES = viewModes(['table', 'list', 'cards', 'grouped']);
type PayslipColumn = 'payroll_run__period_start' | 'net' | 'base_net';

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
const period = (p: CabinetPayslip) => `${fmtDate(p.period_start)} – ${fmtDate(p.period_end)}`;

/** My Space → Payslips: the member's own salary actually paid each period
 *  (read-only, unmasked-for-self). Distinct from Salary History (agreed comp). */
export function CabinetPayslipsPage() {
  const view = usePayslipUi((s) => s.view);
  const setView = usePayslipUi((s) => s.setView);
  const ordering = usePayslipUi((s) => s.ordering);
  const setOrdering = usePayslipUi((s) => s.setOrdering);

  const [page, setPage] = useState(1);
  const { data, isPending, isError, error, isFetching } = useCabinetPayslips({ page, ordering });
  const rows = data?.results ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Payslips</h1>

      <CabinetListToolbar
        view={view}
        onViewChange={setView}
        modes={MODES}
        exportBase={PAYSLIPS_BASE}
        exportParams={{ ordering }}
      />

      {isError ? (
        <p className="text-destructive text-sm">{(error as { message?: string })?.message}</p>
      ) : isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : view === 'table' ? (
        <PayslipTable rows={rows} ordering={ordering} onOrderingChange={setOrdering} />
      ) : view === 'list' ? (
        <PayslipList rows={rows} />
      ) : view === 'cards' ? (
        <PayslipCards rows={rows} />
      ) : (
        <PayslipGroupedByYear rows={rows} />
      )}

      <CabinetListFooter
        count={data?.count ?? 0}
        page={page}
        onPageChange={setPage}
        hasPrev={Boolean(data?.previous)}
        hasNext={Boolean(data?.next)}
        isFetching={isFetching}
        noun={['payslip', 'payslips']}
      />
    </div>
  );
}

function PayslipTable({
  rows,
  ordering,
  onOrderingChange,
}: {
  rows: CabinetPayslip[];
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<PayslipColumn>
              column="payroll_run__period_start"
              value={ordering}
              onChange={onOrderingChange}
            >
              Period
            </SortableTableHead>
            <TableHead className="text-right">Gross</TableHead>
            <TableHead className="hidden text-right sm:table-cell">Deductions</TableHead>
            <SortableTableHead<PayslipColumn>
              column="net"
              value={ordering}
              onChange={onOrderingChange}
              className="text-right"
            >
              Net
            </SortableTableHead>
            <SortableTableHead<PayslipColumn>
              column="base_net"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden text-right md:table-cell"
            >
              Net (USD)
            </SortableTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                No payslips yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((p) => (
              <TableRow key={p.public_id}>
                <TableCell>{period(p)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatMoney(p.gross, p.currency) ?? '—'}
                </TableCell>
                <TableCell className="hidden text-right tabular-nums sm:table-cell">
                  {formatMoney(p.deductions, p.currency) ?? '—'}
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums">
                  {formatMoney(p.net, p.currency) ?? '—'}
                </TableCell>
                <TableCell className="hidden text-right tabular-nums md:table-cell">
                  {formatMoney(p.base_net, 'USD') ?? '—'}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function PayslipList({ rows }: { rows: CabinetPayslip[] }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((p) => (
        <li key={p.public_id} className="flex items-center gap-3 px-3 py-2 text-sm">
          <span className="min-w-0 flex-1 truncate font-semibold">{period(p)}</span>
          <span className="text-muted-foreground hidden sm:inline">Net</span>
          <span className="tabular-nums">{formatMoney(p.net, p.currency) ?? '—'}</span>
        </li>
      ))}
    </ul>
  );
}

function PayslipCards({ rows }: { rows: CabinetPayslip[] }) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((p) => (
        <Card key={p.public_id}>
          <CardHeader>
            <CardTitle className="text-base">{formatMoney(p.net, p.currency) ?? '—'}</CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground space-y-1 text-xs">
            <p>{period(p)}</p>
            <p>Gross: {formatMoney(p.gross, p.currency) ?? '—'}</p>
            <p>Net (USD): {formatMoney(p.base_net, 'USD') ?? '—'}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PayslipGroupedByYear({ rows }: { rows: CabinetPayslip[] }) {
  const groups = useMemo(() => {
    const byYear = new Map<string, CabinetPayslip[]>();
    for (const p of rows) {
      const year = p.period_start ? String(new Date(p.period_start).getFullYear()) : '—';
      (byYear.get(year) ?? byYear.set(year, []).get(year)!).push(p);
    }
    return [...byYear.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [rows]);

  if (rows.length === 0) return <EmptyText />;

  return (
    <div className="space-y-6">
      {groups.map(([year, bucket]) => (
        <section key={year}>
          <h2 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wider uppercase">
            {year}
          </h2>
          <PayslipCards rows={bucket} />
        </section>
      ))}
    </div>
  );
}

function EmptyText() {
  return <CabinetEmptyState>No payslips yet.</CabinetEmptyState>;
}
